import type { ElementRef, OnInit } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import type { Observable } from 'rxjs';
import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, ViewChild, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap, take } from 'rxjs/operators';

import { ESelectedEntityType } from '@common/types/layout.type';
import type { Uuid } from '@common/types';

import type { ExtendedPositionLegacy, TextEntity } from '@sites/entity-core';
import type { TextForm, TextZeroForm } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { NavigationDrawerService } from '@web/layout/drawers/navigation-drawer/navigation-drawer.service';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import { WebEditorModule } from '@web/shared/components/web-editor/web-editor.module';

import { textTransformCss, textWidthCss } from '@sites/entity-core/src/widget/text/styles/text.css-vars';
import { flexAlignVar } from '@sites/entity-core';
import { afterFormValueChange } from '@web/widgets/common/zero-size-sync/after-form-value-change';
import { NewZeroWidgetsService } from '@web/widgets/common/zero-size-sync/new-zero-widgets.service';

/**
 * Editor preview for the `text` widget — signal-based. The form is shared with
 * the control panel via `WebLegacyWidgetSignalFormService` (ref-counted by widget
 * id). The body is a `web-editor` rich-text instance: keystrokes flow out via
 * `changeContent` into the form, and (in a zero block) the rendered height is
 * mirrored back into the extended-position grid.
 *
 * Styling is LESS + CSS custom properties (the css-vars module is shared with
 * the websites-builder renderer).
 */
@Component({
    selector: 'web-text-view',
    templateUrl: './text-view.component.html',
    styleUrls: ['./text-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [WebEditorModule],
})
export class TextViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<TextEntity['data']>>
    implements OnInit
{
    @ViewChild('animatedElem') public editor: ElementRef;

    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly newZeroWidgets: NewZeroWidgetsService = inject(NewZeroWidgetsService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly columnsFacade: ColumnsFacade = inject(ColumnsFacade);
    private readonly sectionsFacade: SectionsFacade = inject(SectionsFacade);
    private readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);

    public form!: TextForm;

    public readonly isDeviceMobile$: Observable<boolean> = this.layoutFacade.isDeviceMobile$;
    public readonly isDeviceTablet$: Observable<boolean> = this.layoutFacade.isDeviceTablet$;
    public readonly selectedEntityId$: Observable<Uuid> = this.layoutFacade.entityId$;

    private readonly isMobile = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    private readonly isTablet = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    private readonly position = computed(() => this.form.position?.().value());
    public readonly isZeroSig = computed<boolean>(() => this.form.value().type === 'zero');

    // --- CSS custom property values (bound on the root/content in the template) ---
    public readonly rootJustify = computed<string>(() =>
        flexAlignVar(this.form.horizontalAlign().value(), 'flex-start')
    );
    public readonly rootTransform = computed<string>(() => textTransformCss(this.position()?.rotate, this.isZeroSig()));
    public readonly rootTransformMobile = computed<string>(() =>
        textTransformCss(this.position()?.mobileRotate, this.isZeroSig())
    );
    public readonly rootTransformTablet = computed<string>(() => {
        const pos = this.position();
        return textTransformCss(pos?.tabletRotate ?? pos?.mobileRotate, this.isZeroSig());
    });
    public readonly rootZIndex = computed<string | null>(() => {
        const zIndex = this.position()?.zIndex;
        return zIndex != null ? `${zIndex}` : null;
    });
    public readonly rootZIndexMobile = computed<string | null>(() => {
        const position = this.position();
        const zIndex = this.form.isSynchronized() ? position?.zIndex : position?.mobileZIndex;
        return zIndex != null ? `${zIndex}` : null;
    });
    public readonly rootZIndexTablet = computed<string | null>(() => {
        const pos = this.position();
        const zIndex = this.form.isSynchronized() ? pos?.zIndex : (pos?.tabletZIndex ?? pos?.mobileZIndex);
        return zIndex != null ? `${zIndex}` : null;
    });
    public readonly contentWidth = computed<string | null>(() =>
        this.isZeroSig() ? null : textWidthCss(this.position()?.width)
    );
    public readonly contentWidthMobile = computed<string | null>(() => textWidthCss(this.position()?.mobileWidth));
    public readonly contentWidthTablet = computed<string | null>(() => {
        const pos = this.position();
        return textWidthCss(pos?.tabletWidth ?? pos?.mobileWidth);
    });

    public widgetIdWithPrefix = computed(() => {
        return `widget_${this.form.id().value()}`;
    });

    /**
     * The first measurement after init corrects a *persisted* size rather than
     * recording a user edit: theme fonts, a different device mode or edited
     * content can all render the text at a height other than the saved one. It
     * still has to reach the store (the zero host's frame and boundary math read
     * the position grid), but it must stay out of the undo stack — otherwise
     * merely opening a page pushes a history entry nobody made, and the first
     * Ctrl+Z appears to do nothing. Cleared once a measurement is committed.
     */
    private isInitialSizeSync = true;

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<TextForm>(this.widget().id, this.destroyRef);

        if (this.isZeroSig()) {
            this.setupZeroHeightSync();
        }
    }

    /**
     * Zero-block text auto-grows with its content AND reflows on width resize:
     * an effect tracks every form change (content edits, width writes from the
     * zero host) and mirrors the rendered editor height back into the
     * extended-position grid (button view pattern). The change-comparison in
     * `setHeight` breaks the write→measure feedback loop.
     */
    private setupZeroHeightSync(): void {
        afterFormValueChange(
            this.injector,
            () => this.form._raw().value(),
            () => {
                const el = this.editor?.nativeElement;
                if (!el) return;
                this.setHeight(el.getBoundingClientRect().height);
            },
            { measureInitial: this.newZeroWidgets.consume(this.widget().id) }
        );
    }

    public onFocus(widgetId: string): void {
        this.selectedEntityId$.pipe(take(1)).subscribe((entityId) => {
            if (entityId && widgetId === entityId) {
                this.layoutFacade.unsetEntity();
                return;
            }

            this.layoutFacade.setSelectedEntity({
                entityType: ESelectedEntityType.widget,
                entityId: widgetId,
                showSettings: false,
            });

            this.revealInNavigation(widgetId);
        });
    }

    /**
     * Reveal the focused text widget in the navigation drawer. The block and
     * section ids aren't on the widget entity, so they're resolved on-demand
     * from `widget.columnId` (column → section → block) when focus fires.
     */
    private revealInNavigation(widgetId: string): void {
        const widget = this.widget();

        this.columnsFacade
            .columnById$(widget.columnId)
            .pipe(
                take(1),
                switchMap((column) =>
                    this.sectionsFacade.sectionById$(column?.parentId).pipe(
                        take(1),
                        map((section) => ({ column, section }))
                    )
                )
            )
            .subscribe(({ column, section }) => {
                this.navDrawerService.selectInNavigation({
                    entityId: widgetId,
                    blockId: section?.parentId,
                    sectionId: column?.parentId,
                    columnId: widget.columnId,
                    gridId: widget.structureId,
                });
            });
    }

    /**
     * Zero-block text auto-grows with its content: mirror the rendered editor
     * height into the extended-position grid (per active device) so the zero
     * host's selection frame and boundary math track the real size. The change-
     * comparison breaks the write→measure feedback loop (button view pattern).
     */
    private setHeight(value: number): void {
        if (!Number.isFinite(value) || value <= 0) return;

        const zeroForm = this.form as TextZeroForm;
        // The form's position accessor is union-typed (simple grid on default
        // placement) — this path only runs for zero forms.
        const positionField = this.form.position as unknown as FieldTree<ExtendedPositionLegacy>;
        const position = positionField().value();

        const write = () => {
            if (this.isMobile()) {
                if (position.mobileHeight !== value) positionField.mobileHeight().value.set(value);
                return;
            }
            if (this.isTablet()) {
                if (position.tabletHeight !== value) positionField.tabletHeight().value.set(value);
                return;
            }
            if (position.mobileHeight !== value) positionField.mobileHeight().value.set(value);
            if (position.tabletHeight !== value) positionField.tabletHeight().value.set(value);
            if (position.height !== value) positionField.height().value.set(value);
        };

        if (zeroForm.inTransaction()) {
            // An outer transaction (settings-panel session, host gesture) owns the
            // emission — the plugin doesn't nest, so just contribute the writes.
            write();
        } else if (this.isInitialSizeSync) {
            zeroForm.transactWithoutHistory(write);
        } else {
            zeroForm.transact(write);
        }

        this.isInitialSizeSync = false;
    }
}
